import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import { useTheme } from '../../contexts/ThemeContext';
import { DEV_MODE } from '../../config/environment';

export const ADMIN_SIDEBAR_WIDTH = 260;
export const ADMIN_SIDEBAR_COLLAPSED_WIDTH = 72;
const SIDEBAR_EXPAND_MS = 320;
const SIDEBAR_WIDTH_TRANSITION = `width ${SIDEBAR_EXPAND_MS}ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow ${SIDEBAR_EXPAND_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
const SIDEBAR_LABEL_TRANSITION = `opacity ${SIDEBAR_EXPAND_MS * 0.9}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${SIDEBAR_EXPAND_MS * 0.9}ms cubic-bezier(0.4, 0, 0.2, 1)`;

export const ADMIN_NAV_CATEGORIES = [
  {
    id: 'overview',
    label: 'Overview',
    icon: 'grid-outline',
    color: '#6a9fd4',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: 'speedometer-outline' },
    ],
  },
  {
    id: 'venues',
    label: 'Venues',
    icon: 'business-outline',
    color: '#9a86b8',
    items: [
      { id: 'venues', label: 'All Venues', icon: 'list-outline' },
    ],
  },
  {
    id: 'submissions',
    label: 'Submissions',
    icon: 'clipboard-outline',
    color: '#c9925e',
    items: [
      { id: 'new_venues', label: 'New Venues', icon: 'add-circle-outline' },
      { id: 'venue_reports', label: 'Venue Reports', icon: 'document-text-outline', reportType: 'venue' },
      { id: 'venue_spec_flags', label: 'Spec Flags', icon: 'flag-outline', reportType: 'spec_flag' },
      { id: 'venue_interest_tags', label: 'Venue Interests', icon: 'heart-outline', reportType: 'venue_interest_tag' },
      { id: 'resource_submissions', label: 'Resources', icon: 'link-outline', reportType: 'resource_submission' },
      { id: 'venue_document_submissions', label: 'Document Uploads', icon: 'cloud-upload-outline', reportType: 'venue_document_submission' },
      { id: 'venue_archive_requests', label: 'Archive Requests', icon: 'archive-outline', reportType: 'venue_archive_request' },
    ],
  },
  {
    id: 'people',
    label: 'Users & Community',
    icon: 'people-outline',
    color: '#6bb5a8',
    items: [
      { id: 'users', label: 'Users', icon: 'person-outline' },
      { id: 'community', label: 'Community', icon: 'chatbubbles-outline' },
      { id: 'interests', label: 'Interests', icon: 'heart-outline' },
      { id: 'feature_notify', label: 'Notify me', icon: 'mail-outline' },
    ],
  },
  {
    id: 'fund',
    label: 'Mutual Fund',
    icon: 'heart-circle-outline',
    color: '#dba32d',
    items: [
      { id: 'mutual_fund', label: 'Mutual Fund', icon: 'wallet-outline' },
    ],
  },
  {
    id: 'site',
    label: 'Site & Data',
    icon: 'server-outline',
    color: '#8a96a8',
    items: [
      { id: 'site_operations', label: 'Operations', icon: 'settings-outline' },
      { id: 'change_logs', label: 'Change Logs', icon: 'newspaper-outline' },
      { id: 'site_data', label: 'Data & Imports', icon: 'cloud-upload-outline' },
      { id: 'site_locations', label: 'Locations', icon: 'map-outline' },
      { id: 'site_dev_tools', label: 'Dev Tools', icon: 'code-working-outline' },
    ],
  },
];

export function getAdminNavCategories() {
  return ADMIN_NAV_CATEGORIES.map((category) => ({
    ...category,
    items: category.items.filter((item) => item.id !== 'site_dev_tools' || DEV_MODE),
  })).filter((category) => category.items.length > 0);
}

const hexToRgba = (hex, alpha) => {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((c) => c + c).join('')
    : normalized;
  const int = parseInt(full, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function AdminSidebar({ activeTab, onSelectTab, collapsed, onCollapsedChange }) {
  const navigation = useNavigation();
  const { colors } = useAdminThemeContext();
  const { isDarkMode, toggleTheme } = useTheme();
  const useHoverExpand = Platform.OS === 'web';
  const [internalCollapsed, setInternalCollapsed] = useState(true);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const isCollapsed = useHoverExpand
    ? !hoverExpanded
    : (collapsed ?? internalCollapsed);
  const setCollapsed = onCollapsedChange ?? setInternalCollapsed;
  const showLabels = useHoverExpand ? hoverExpanded : !isCollapsed;
  const showExpandedNav = useHoverExpand ? hoverExpanded : !isCollapsed;
  const showCollapsedNav = useHoverExpand ? !hoverExpanded : isCollapsed;
  const expandLabelStyle = useHoverExpand && hoverExpanded && Platform.OS === 'web'
    ? {
      opacity: hoverExpanded ? 1 : 0,
      transform: hoverExpanded ? 'translateX(0px)' : 'translateX(-8px)',
      transition: SIDEBAR_LABEL_TRANSITION,
      whiteSpace: 'nowrap',
    }
    : null;

  const navCategories = useMemo(() => getAdminNavCategories(), []);

  const [expandedCategories, setExpandedCategories] = useState(() =>
    Object.fromEntries(navCategories.map((category) => [category.id, false]))
  );

  const toggleCategory = (categoryId) => {
    if (isCollapsed && !useHoverExpand) {
      setCollapsed(false);
      setExpandedCategories((prev) => ({ ...prev, [categoryId]: true }));
      return;
    }
    setExpandedCategories((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const handleSelectItem = (item) => {
    onSelectTab(item.id, item.reportType ? { reportType: item.reportType } : undefined);
  };

  const sidebarWidth = isCollapsed ? ADMIN_SIDEBAR_COLLAPSED_WIDTH : ADMIN_SIDEBAR_WIDTH;

  const themed = useMemo(
    () =>
      StyleSheet.create({
        sidebar: {
          backgroundColor: colors.sidebarBg,
          borderRightColor: colors.sidebarBorder,
        },
        sidebarHeader: {
          borderBottomColor: colors.divider,
        },
        sidebarTitle: {
          color: colors.textPrimary,
        },
        toggleButton: {
          backgroundColor: colors.cardBgSubtle,
        },
        categoryHeaderActive: {
          backgroundColor: colors.sidebarCategoryActive,
        },
        categoryLabel: {
          color: colors.iconMuted,
        },
        categoryLabelActive: {
          color: colors.textPrimary,
        },
        navItemActive: {
          backgroundColor: colors.navActive,
        },
        navItemText: {
          color: colors.navItemText,
        },
        navItemTextActive: {
          color: colors.textOnAccent,
        },
        collapsedNavItemActive: {
          backgroundColor: colors.navActive,
        },
        sidebarFooter: {
          borderTopColor: colors.divider,
        },
        homeButton: {
          backgroundColor: colors.cardBgSubtle,
          borderColor: colors.cardBorder,
        },
        homeButtonText: {
          color: colors.textPrimary,
        },
      }),
    [colors]
  );

  return (
    <View
      nativeID="admin-sidebar-root"
      onMouseEnter={useHoverExpand ? () => setHoverExpanded(true) : undefined}
      onMouseLeave={useHoverExpand ? () => setHoverExpanded(false) : undefined}
      style={[
        styles.sidebar,
        themed.sidebar,
        { width: sidebarWidth, minWidth: sidebarWidth },
        Platform.OS === 'web' && {
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: 'auto',
          zIndex: 150,
          overflow: 'hidden',
          transition: SIDEBAR_WIDTH_TRANSITION,
          ...(useHoverExpand && hoverExpanded
            ? { boxShadow: '4px 0 24px rgba(0,0,0,0.35)' }
            : { boxShadow: '2px 0 12px rgba(0,0,0,0.15)' }),
        },
      ]}
    >
      <View style={[styles.sidebarHeader, themed.sidebarHeader, showCollapsedNav && styles.sidebarHeaderCollapsed]}>
        <View style={[styles.sidebarTitleRow, showCollapsedNav && styles.sidebarTitleRowCollapsed]}>
          <Ionicons name="shield-checkmark-outline" size={20} color={colors.accentGreen} />
          {showLabels && (
            <Text style={[styles.sidebarTitle, themed.sidebarTitle, expandLabelStyle]}>
              Admin
            </Text>
          )}
        </View>
        {!useHoverExpand && (
          <TouchableOpacity
            onPress={() => setCollapsed(!isCollapsed)}
            style={[styles.toggleButton, themed.toggleButton, isCollapsed && styles.toggleButtonCollapsed]}
            accessibilityLabel={isCollapsed ? 'Expand admin sidebar' : 'Collapse admin sidebar'}
          >
            <Ionicons
              name={isCollapsed ? 'chevron-forward' : 'chevron-back'}
              size={18}
              color={colors.textPrimary}
            />
          </TouchableOpacity>
        )}
      </View>

      <ScrollView
        style={styles.navScroll}
        contentContainerStyle={[
          styles.navScrollContent,
          showCollapsedNav && styles.navScrollContentCollapsed,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {navCategories.map((category) => {
          const isExpanded = expandedCategories[category.id];
          const categoryHasActive = category.items.some((item) => item.id === activeTab);
          const categoryColor = category.color || colors.accentGreen;

          return (
            <View
              key={category.id}
              style={[
                styles.categoryBlock,
                showCollapsedNav && styles.categoryBlockCollapsed,
                showExpandedNav && {
                  borderLeftWidth: 2,
                  borderLeftColor: hexToRgba(categoryColor, categoryHasActive ? 0.75 : 0.35),
                  paddingLeft: 8,
                  marginLeft: 2,
                },
              ]}
            >
              <TouchableOpacity
                style={[
                  styles.categoryHeader,
                  showCollapsedNav && styles.categoryHeaderCollapsed,
                  categoryHasActive && {
                    backgroundColor: hexToRgba(categoryColor, showCollapsedNav ? 0.14 : 0.08),
                  },
                ]}
                onPress={() => toggleCategory(category.id)}
              >
                <View style={[
                  styles.categoryHeaderLeft,
                  showCollapsedNav && styles.categoryHeaderLeftCollapsed,
                ]}>
                  <Ionicons
                    name={category.icon}
                    size={16}
                    color={categoryHasActive ? categoryColor : colors.iconMuted}
                  />
                  {(showLabels) && (
                    <Text
                      style={[
                        styles.categoryLabel,
                        themed.categoryLabel,
                        { color: categoryHasActive ? categoryColor : colors.iconMuted },
                        expandLabelStyle,
                      ]}
                      numberOfLines={1}
                    >
                      {category.label}
                    </Text>
                  )}
                </View>
                {showLabels && (
                  <Ionicons
                    name={isExpanded ? 'chevron-down' : 'chevron-forward'}
                    size={14}
                    color={colors.iconSubtle}
                    style={expandLabelStyle}
                  />
                )}
              </TouchableOpacity>

              {showExpandedNav && isExpanded && (
                <View style={styles.itemsList}>
                  {category.items.map((item) => {
                    const isActive = activeTab === item.id;
                    return (
                      <TouchableOpacity
                        key={item.id}
                        style={[
                          styles.navItem,
                          isActive && {
                            backgroundColor: hexToRgba(categoryColor, 0.16),
                            borderWidth: 1,
                            borderColor: hexToRgba(categoryColor, 0.28),
                          },
                        ]}
                        onPress={() => handleSelectItem(item)}
                      >
                        <Ionicons
                          name={item.icon}
                          size={16}
                          color={isActive ? categoryColor : colors.iconMuted}
                          style={styles.navItemIcon}
                        />
                        <Text
                          style={[
                            styles.navItemText,
                            themed.navItemText,
                            isActive && { color: categoryColor, fontWeight: '600' },
                            expandLabelStyle,
                          ]}
                          numberOfLines={1}
                        >
                          {item.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}

              {showCollapsedNav && isExpanded && (
                <View style={styles.collapsedItems}>
                  {category.items.map((item) => {
                    const isActive = activeTab === item.id;
                    return (
                      <TouchableOpacity
                        key={item.id}
                        style={[
                          styles.collapsedNavItem,
                          isActive && {
                            backgroundColor: hexToRgba(categoryColor, 0.2),
                          },
                        ]}
                        onPress={() => handleSelectItem(item)}
                        accessibilityLabel={item.label}
                      >
                        <Ionicons
                          name={item.icon}
                          size={18}
                          color={isActive ? categoryColor : colors.iconMuted}
                        />
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>

      <View style={[styles.sidebarFooter, themed.sidebarFooter, { backgroundColor: colors.sidebarBg }]}>
        <View style={[styles.footerRow, showCollapsedNav && styles.footerRowCollapsed]}>
          <TouchableOpacity
            style={[
              styles.homeButton,
              themed.homeButton,
              showCollapsedNav ? styles.homeButtonCollapsed : styles.homeButtonExpanded,
            ]}
            onPress={() => navigation.navigate('Home')}
            accessibilityLabel="Back to home screen"
          >
            <Ionicons name="home-outline" size={18} color={colors.accentGreen} />
            {showLabels && (
              <Text
                style={[styles.homeButtonText, themed.homeButtonText, expandLabelStyle]}
                numberOfLines={1}
              >
                Back to Home
              </Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.homeButton,
              themed.homeButton,
              showCollapsedNav ? styles.homeButtonCollapsed : styles.homeButtonExpanded,
            ]}
            onPress={toggleTheme}
            accessibilityLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <Ionicons
              name={isDarkMode ? 'sunny-outline' : 'moon-outline'}
              size={18}
              color={colors.accentGreen}
            />
            {showLabels && (
              <Text
                style={[styles.homeButtonText, themed.homeButtonText, expandLabelStyle]}
                numberOfLines={1}
              >
                {isDarkMode ? 'Light mode' : 'Dark mode'}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    borderRightWidth: 1,
    flexDirection: 'column',
    flexShrink: 0,
    alignSelf: 'stretch',
    height: '100%',
    zIndex: 200,
    ...(Platform.OS === 'web' && {
      boxShadow: '2px 0 12px rgba(0,0,0,0.15)',
    }),
  },
  sidebarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    flexShrink: 0,
  },
  sidebarHeaderCollapsed: {
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  sidebarTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  sidebarTitleRowCollapsed: {
    justifyContent: 'center',
    flex: 0,
    alignSelf: 'center',
  },
  sidebarTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  toggleButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  toggleButtonCollapsed: {
    alignSelf: 'center',
    marginHorizontal: 'auto',
  },
  navScroll: {
    flex: 1,
    minHeight: 0,
  },
  navScrollContent: {
    paddingTop: 12,
    paddingBottom: 32,
    paddingHorizontal: 8,
    flexGrow: 1,
  },
  navScrollContentCollapsed: {
    paddingHorizontal: 4,
    alignItems: 'center',
  },
  categoryBlock: {
    marginBottom: 8,
    width: '100%',
    overflow: 'hidden',
  },
  categoryBlockCollapsed: {
    alignItems: 'center',
  },
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  categoryHeaderCollapsed: {
    justifyContent: 'center',
    alignSelf: 'center',
    width: 44,
    paddingHorizontal: 0,
  },
  categoryHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  categoryHeaderLeftCollapsed: {
    flex: 0,
    justifyContent: 'center',
  },
  categoryLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    flexShrink: 1,
  },
  itemsList: {
    paddingLeft: 4,
    paddingBottom: 4,
    marginTop: 2,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginBottom: 2,
    minWidth: 0,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  navItemIcon: {
    marginRight: 10,
    width: 18,
  },
  navItemText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
    minWidth: 0,
  },
  collapsedItems: {
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
    marginBottom: 4,
    width: '100%',
  },
  collapsedNavItem: {
    width: 44,
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    overflow: 'hidden',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  sidebarFooter: {
    borderTopWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 10,
    flexShrink: 0,
    marginTop: 'auto',
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      bottom: 0,
      zIndex: 5,
    }),
  },
  footerRow: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
    width: '100%',
  },
  footerRowCollapsed: {
    alignItems: 'center',
  },
  homeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  homeButtonExpanded: {
    width: '100%',
    justifyContent: 'flex-start',
    minWidth: 0,
  },
  homeButtonCollapsed: {
    width: 44,
    height: 40,
    paddingHorizontal: 0,
    paddingVertical: 0,
    justifyContent: 'center',
    alignSelf: 'center',
  },
  homeButtonText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    fontWeight: '600',
    flexShrink: 1,
  },
});
